import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRecommendedComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const recommended = getRecommendedComics().filter((c) => c.id !== id).slice(0, 5);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">未找到该漫画</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    image: comic.coverImage,
    url: `https://www.hanman-site.com/comic/${comic.id}`,
    genre: comic.category,
    dateModified: comic.updateDate,
    inLanguage: 'zh-Hans',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[
              { name: '首页', path: '/' },
              { name: comic.category, path: `/category/${comic.categorySlug}` },
              { name: comic.title },
            ]} />
          </div>

          <div className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="flex-shrink-0 w-full md:w-[260px]">
              <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                  title={`${comic.title} - 漫画免费韩漫`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3"
                style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-500">{comic.author} 著</span>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500">{comic.category}</span>
                <span className="text-foreground-300">·</span>
                <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-500' : 'text-secondary-500'}`}>
                  {comic.status}
                </span>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  <span className="text-lg font-bold text-foreground-800">{comic.rating}</span>
                </div>
                <span className="text-xs text-foreground-400">/ 10</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to={`/comic/${comic.id}/read/1`}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
                <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-1.5"></i>收藏
                </button>
              </div>

              <div className="bg-background-100 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'info' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    作品简介
                  </button>
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    章节列表 ({comic.chapters.length})
                  </button>
                </div>

                {activeTab === 'info' && (
                  <div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {showFullDescription
                        ? comic.description
                        : comic.description.slice(0, 150) + (comic.description.length > 150 ? '...' : '')}
                    </p>
                    {comic.description.length > 150 && (
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-2 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                      >
                        {showFullDescription ? '收起' : '展开全部'}
                      </button>
                    )}
                  </div>
                )}

                {activeTab === 'chapters' && (
                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}/read/${chapter.chapterNumber}`}
                        className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-background-200 transition-colors cursor-pointer group"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {recommended.length > 0 && (
            <section>
              <div className="flex items-end justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900"
                  style={{ fontFamily: 'var(--font-heading)' }}>
                  猜你喜欢
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {recommended.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}